import { boardCollisionDropPosition } from "@components/modules/resource-planning/tasks/kanban/utils";
import type {
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { t } from "@lingui/macro";
import {
  MoveTaskOnBoardDocument,
  type MoveTaskOnBoardMutation,
  type MoveTaskOnBoardMutationVariables,
  type TaskBaseFragment,
  TaskBoardGroupByEnum,
} from "@src/__generated__/graphql";
import type { TasksKanbanStore } from "@src/components/modules/resource-planning/tasks/kanban/store";
import type { TaskBoardColumnStore } from "@src/components/modules/resource-planning/tasks/kanban/store/column";
import type {
  BoardCardDragBlockReason,
  BoardColumnDropHighlight,
  BoardDragData,
  BoardDropIndicator,
  BoardMoveBlockedNotice,
} from "@src/components/modules/resource-planning/tasks/kanban/types";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { GraphQLFormattedError } from "graphql";
import { action, computed, makeObservable, observable } from "mobx";

/**
 * Card drag and drop on the board — columns are drop targets only. Owns the
 * drag session, the pickup/drop guards, the insertion line and the
 * optimistic card moves; the board keeps the columns and counts.
 */
export class TaskBoardDragStore {
  @observable dragKind: "card" | null = null;
  @observable dragTaskId: string | null = null;
  @observable dragSourceColumnKey: string | null = null;
  @observable dragOverColumnKey: string | null = null;
  /** Set at pickup when a guard blocks this card — the drag does nothing. */
  @observable dragBlockReason: BoardCardDragBlockReason | null = null;
  /** Insertion line position for same-column reorder (6.1/6.2). */
  @observable dropIndicator: BoardDropIndicator | null = null;

  /** Drag end swallows the release click — suppress the card open right after. */
  private lastDragEndAtMs = 0;
  /** Wall-clock drag start, to tell a shaky click from a real drag. */
  private dragStartedAtMs = 0;

  private moveTaskMutator = new MutationHelper<
    MoveTaskOnBoardMutation,
    MoveTaskOnBoardMutationVariables
  >(MoveTaskOnBoardDocument);

  constructor(
    private readonly boardStore: TasksKanbanStore,
    private readonly appStore: AppStore,
  ) {
    makeObservable(this);
  }

  @computed
  get isDragActive(): boolean {
    return this.dragKind !== null;
  }

  @computed
  get draggedTask(): {
    task: TaskBaseFragment;
    column: TaskBoardColumnStore;
  } | null {
    if (!this.dragTaskId || !this.dragSourceColumnKey) return null;
    // A blocked drag has no target: no overlay, no highlight, no line.
    if (this.dragBlockReason) return null;

    const column = this.findColumn(this.dragSourceColumnKey);
    const task = column?.tasks.find(
      (boardTask) => boardTask.id === this.dragTaskId,
    );

    return column && task ? { task, column } : null;
  }

  /** The pointerup that ends a drag also fires click — the card must ignore it. */
  isDragReleaseClick(): boolean {
    return Date.now() - this.lastDragEndAtMs < 200;
  }

  /**
   * Cross-column moves stay allowed under any sorting/filters — they change a
   * field value, not the order. Same-column drops need the manual order.
   * Assignee columns only reorder: a move to another column would reassign
   * the task (also rejected server-side).
   */
  canDropCardInColumn(
    sourceColumn: TaskBoardColumnStore,
    destColumn: TaskBoardColumnStore,
  ): boolean {
    if (sourceColumn.key === destColumn.key) return true;

    return this.boardStore.groupBy !== TaskBoardGroupByEnum.Assignee;
  }

  /** Three-state column highlight while a card is dragged (phase 6.1). */
  columnDropHighlight(
    column: TaskBoardColumnStore,
  ): BoardColumnDropHighlight | null {
    if (this.dragKind !== "card") return null;

    const dragged = this.draggedTask;
    if (!dragged) return null;

    if (this.dragOverColumnKey !== column.key) return "neutral";

    return this.canDropCardInColumn(dragged.column, column)
      ? "valid"
      : "blocked";
  }

  @action.bound
  handleBoardDragStart(event: DragStartEvent): void {
    const data = event.active.data.current as BoardDragData | undefined;
    if (data?.type !== "card" || !data.taskId) return;

    this.dragKind = "card";
    this.dragStartedAtMs = Date.now();

    this.dragTaskId = data.taskId;
    this.dragSourceColumnKey = data.columnKey;
    this.dragOverColumnKey = data.columnKey;

    // Resolve the guard at pickup so a blocked drag draws no overlay,
    // no column highlight and no insertion line. The alert comes on release.
    const dragged = this.draggedTask;
    if (!dragged) return;

    this.dragBlockReason = this.cardDragBlockReason();
  }

  @action.bound
  handleBoardDragOver(event: DragOverEvent): void {
    if (this.dragKind !== "card") return;

    const data = event.over?.data.current as BoardDragData | undefined;
    this.dragOverColumnKey = data?.columnKey ?? null;
    this.updateDropIndicator(event);
  }

  /**
   * Drag over only fires when the target changes — crossing a card's
   * midpoint keeps the same target, so the line side is updated per move.
   */
  @action.bound
  handleBoardDragMove(event: DragMoveEvent): void {
    if (this.dragKind !== "card") return;

    this.updateDropIndicator(event);
  }

  @action.bound
  handleBoardDragCancel(): void {
    this.reset();
  }

  @action.bound
  handleBoardDragEnd(event: DragEndEvent): void {
    const data = event.active.data.current as BoardDragData | undefined;

    if (data?.type !== "card") {
      this.reset();
      return;
    }

    const { dragTaskId, dragSourceColumnKey, dropIndicator, dragBlockReason } =
      this;
    this.reset();
    if (!dragTaskId || !dragSourceColumnKey) return;

    // A fast, tiny "drag" is a shaky click — the sensor activated and its
    // pointer capture swallowed the real click event, so open the task here.
    // Checked before the guards: a click on a locked card is still a click.
    const isWiggleClick =
      Date.now() - this.dragStartedAtMs < 250 &&
      Math.hypot(event.delta.x, event.delta.y) < 20;
    if (isWiggleClick) {
      this.boardStore.handleTaskClick(dragTaskId);
      return;
    }

    this.lastDragEndAtMs = Date.now();

    // Nothing visually marks the card as locked, so the toast explains why
    // the drag had no effect (phase 6.3).
    if (dragBlockReason) {
      this.boardStore.showMoveBlockedToast(
        this.dragBlockNotice(dragBlockReason),
      );
      return;
    }
    void this.handleCardDrop(
      dragTaskId,
      dragSourceColumnKey,
      event.over,
      dropIndicator,
    );
  }

  @action.bound
  reset(): void {
    this.dragKind = null;
    this.dragTaskId = null;
    this.dragSourceColumnKey = null;
    this.dragOverColumnKey = null;
    this.dragBlockReason = null;
    this.dropIndicator = null;
  }

  private findColumn(key: string): TaskBoardColumnStore | undefined {
    return this.boardStore.columns.find((column) => column.key === key);
  }

  private cardDragBlockReason(): BoardCardDragBlockReason | null {
    // Dragging is off entirely outside manual order — a cross-column drop
    // would have to write a position into an order nobody can see.
    if (!this.boardStore.canReorderCards) return "sorting";

    return null;
  }

  private dragBlockNotice(
    reason: BoardCardDragBlockReason,
  ): BoardMoveBlockedNotice {
    switch (reason) {
      case "sorting":
        return this.boardStore.reorderBlockedNotice ?? { title: "" };
    }
  }

  /**
   * Insertion line for the drop target, in the source column (reorder) as
   * well as another column. The side comes from which half of the target
   * card the pointer is in (see `boardCollisionDetection`). An empty column
   * has no card to line up against.
   */
  private updateDropIndicator(event: DragMoveEvent | DragOverEvent): void {
    const data = event.over?.data.current as BoardDragData | undefined;
    const dragged = this.draggedTask;

    if (!data || !dragged || !event.over) {
      this.setDropIndicator(null);
      return;
    }

    const destColumn = this.findColumn(data.columnKey);
    if (!destColumn) {
      this.setDropIndicator(null);
      return;
    }

    const isSameColumn = destColumn.key === dragged.column.key;
    if (
      !isSameColumn &&
      !this.canDropCardInColumn(dragged.column, destColumn)
    ) {
      this.setDropIndicator(null);
      return;
    }

    const position = boardCollisionDropPosition(
      event.collisions,
      event.over.id,
    );
    if (
      data.type !== "card" ||
      !data.taskId ||
      data.taskId === dragged.task.id ||
      !position
    ) {
      this.setDropIndicator(null);
      return;
    }

    this.setDropIndicator({
      columnKey: destColumn.key,
      taskId: data.taskId,
      position,
    });
  }

  @action
  private setDropIndicator(indicator: BoardDropIndicator | null): void {
    // Skip no-op writes — drag move fires per mousemove.
    if (
      this.dropIndicator?.columnKey === indicator?.columnKey &&
      this.dropIndicator?.taskId === indicator?.taskId &&
      this.dropIndicator?.position === indicator?.position
    ) {
      return;
    }

    this.dropIndicator = indicator;
  }

  /**
   * Both a same-column reorder and a move to another column land where the
   * insertion line showed, with the rank taken from the two visible
   * neighbours there. Without a line (empty column, blocked drop) a
   * cross-column drop falls back to the top, like kebab/detail/bulk do.
   */
  private async handleCardDrop(
    taskId: string,
    sourceColumnKey: string,
    over: DragEndEvent["over"],
    indicator: BoardDropIndicator | null,
  ): Promise<void> {
    const overData = over?.data.current as BoardDragData | undefined;
    if (!overData) return;

    const sourceColumn = this.findColumn(sourceColumnKey);
    const destColumn = this.findColumn(overData.columnKey);
    const task = sourceColumn?.tasks.find((card) => card.id === taskId);
    if (!sourceColumn || !destColumn || !task) return;

    if (sourceColumn.key === destColumn.key) {
      const oldIndex = sourceColumn.tasks.findIndex(
        (card) => card.id === taskId,
      );
      if (oldIndex === -1) return;

      // Land exactly where the insertion line showed.
      let newIndex: number | null = null;
      if (indicator?.columnKey === sourceColumn.key) {
        const overIndex = sourceColumn.tasks.findIndex(
          (card) => card.id === indicator.taskId,
        );
        if (overIndex !== -1) {
          newIndex = overIndex + (indicator.position === "after" ? 1 : 0);
          // Removal shifts everything after the old spot down by one.
          if (newIndex > oldIndex) newIndex -= 1;
        }
      }
      if (newIndex === null || newIndex === oldIndex) return;

      await this.reorderCardInColumn(taskId, sourceColumn, newIndex);
      return;
    }

    if (!this.canDropCardInColumn(sourceColumn, destColumn)) {
      this.boardStore.showMoveBlockedToast({
        title: t`Tasks can't be moved between assignees.`,
        description: t`Change the assignees in the task detail.`,
      });
      return;
    }

    // No insertion line (empty column, column body) means the top.
    let insertIndex = 0;
    if (indicator?.columnKey === destColumn.key) {
      const overIndex = destColumn.tasks.findIndex(
        (card) => card.id === indicator.taskId,
      );
      if (overIndex !== -1) {
        insertIndex = overIndex + (indicator.position === "after" ? 1 : 0);
      }
    }

    await this.moveCardToColumn(task, sourceColumn, destColumn, insertIndex);
  }

  /** Optimistic reorder: apply, persist, roll back on error — no refetch. */
  @action.bound
  private async reorderCardInColumn(
    taskId: string,
    column: TaskBoardColumnStore,
    newIndex: number,
  ): Promise<void> {
    const snapshot = column.snapshot();
    column.moveTaskToIndex(taskId, newIndex);

    const tasks = column.tasks;
    const index = tasks.findIndex((card) => card.id === taskId);
    const { afterTaskId, beforeTaskId } = this.boardStore.moveAnchors(
      tasks.filter((card) => card.id !== taskId),
      index,
    );

    const [, error] = await this.moveTaskMutator.mutate(
      {
        input: {
          task_id: taskId,
          // The task's own value, not the column key — the Complete column
          // groups several done statuses and must not rewrite them.
          group_value: this.boardStore.taskGroupValue(taskId, column),
          source_assignee_user_id: this.boardStore.assigneeColumnUserId(column),
          before_task_id: beforeTaskId,
          after_task_id: afterTaskId,
          board_key: this.boardStore.boardKey,
          group_by: this.boardStore.groupBy,
        },
      },
      undefined,
      true,
    );
    if (!error) return;

    column.restore(snapshot);
    this.handleMoveError(error);
  }

  /** Optimistic cross-column move to the given index of the target column. */
  @action.bound
  private async moveCardToColumn(
    task: TaskBaseFragment,
    sourceColumn: TaskBoardColumnStore,
    destColumn: TaskBoardColumnStore,
    insertIndex: number,
  ): Promise<void> {
    const { afterTaskId, beforeTaskId } = this.boardStore.moveAnchors(
      destColumn.tasks,
      insertIndex,
    );
    const sourceSnapshot = sourceColumn.snapshot();
    const destSnapshot = destColumn.snapshot();
    // Claimed before the optimistic change, so a refresh still in flight from
    // an earlier drag cannot put the old counts back.
    const columnKeys = [sourceColumn.key, destColumn.key];
    const countsVersion = this.boardStore.claimColumnCounts(columnKeys);

    sourceColumn.removeTask(task.id);
    if (destColumn.hasLoadedOnce) {
      destColumn.insertTaskAt(task, insertIndex);
    } else {
      destColumn.adjustTotals(task, 1);
    }

    const [data, error] = await this.moveTaskMutator.mutate(
      {
        input: {
          task_id: task.id,
          group_value: this.boardStore.columnGroupValue(destColumn),
          after_task_id: afterTaskId,
          before_task_id: beforeTaskId,
          board_key: this.boardStore.boardKey,
          group_by: this.boardStore.groupBy,
        },
      },
      undefined,
      true,
    );

    if (error || !data) {
      sourceColumn.restore(sourceSnapshot);
      destColumn.restore(destSnapshot);
      this.handleMoveError(error);
      void this.boardStore.fetchColumnsByKeys(columnKeys, countsVersion);
      return;
    }

    this.boardStore.patchBoardTask(task.id, data.moveTaskOnBoard);
    // The optimistic totals use task-wide time — the server has the real split.
    void this.boardStore.fetchColumnsByKeys(columnKeys, countsVersion);
  }

  private handleMoveError(
    error: readonly GraphQLFormattedError[] | Error | null,
  ): void {
    if (!error) return;

    const messages =
      error instanceof Error
        ? [error.message]
        : error.map((graphqlError) => graphqlError.message);

    for (const message of messages) {
      this.appStore.UIStore.toast({ status: "error", title: message });
    }
  }
}
